import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

// Initialize state - if no token, start with loading false to show login immediately
const getInitialState = () => {
  const token = localStorage.getItem('token');
  return {
    user: null,
    token: token,
    loading: !!token, // Only load if there's a token to verify
    error: null
  };
};

const initialState = getInitialState();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: action.payload
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set up api interceptor for token
  useEffect(() => {
    if (state.token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [state.token]);

  // Check if user is logged in on app start
  useEffect(() => {
    let isMounted = true;
    
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        // No token, immediately set user as not logged in
        if (isMounted) {
          dispatch({ type: 'AUTH_FAILURE', payload: null });
        }
        return;
      }

      // Token exists, verify it with server
      try {
        const response = await api.get('/auth/me');
        if (isMounted && response.data && response.data.user) {
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user: response.data.user,
              token: token
            }
          });
        } else {
          // Invalid response, clear token
          if (isMounted) {
            localStorage.removeItem('token');
            dispatch({ type: 'AUTH_FAILURE', payload: null });
          }
        }
      } catch (error) {
        // Server error or invalid token - clear token
        if (isMounted) {
          localStorage.removeItem('token');
          dispatch({ type: 'AUTH_FAILURE', payload: null });
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // Track ongoing login request to prevent duplicates (no cooldown for development)
  const loginRequestRef = useRef(null);
  
  const login = useCallback(async (email, password) => {
    // If a login request is already in progress, return the existing promise
    if (loginRequestRef.current) {
      return loginRequestRef.current;
    }
    
    // Create the login promise
    const loginPromise = (async () => {
      try {
        dispatch({ type: 'AUTH_START' });
        
        const response = await api.post('/auth/login', { email, password });
        const { token, user } = response.data;
        
        localStorage.setItem('token', token);
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user, token }
        });
        
        toast.success('Login successful!');
        
        // Clear the request ref after success
        loginRequestRef.current = null;
        
        return { success: true };
      } catch (error) {
        // Clear the request ref on error
        loginRequestRef.current = null;
        
        // Handle rate limiting - just show error but don't block
        if (error.response?.status === 429) {
          const message = error.response?.data?.message || 'Too many login attempts. Please try again.';
          dispatch({ type: 'AUTH_FAILURE', payload: message });
          toast.error(message);
          return { success: false, error: message };
        }
        
        const message = error.response?.data?.message || 'Login failed';
        dispatch({ type: 'AUTH_FAILURE', payload: message });
        toast.error(message);
        return { success: false, error: message };
      }
    })();
    
    // Store the promise
    loginRequestRef.current = loginPromise;
    
    return loginPromise;
  }, []);

  // Track ongoing register request to prevent duplicates
  const registerRequestRef = useRef(null);
  
  const register = useCallback(async (userData) => {
    // If a register request is already in progress, return the existing promise
    if (registerRequestRef.current) {
      return registerRequestRef.current;
    }
    
    // Create the register promise
    const registerPromise = (async () => {
      try {
        dispatch({ type: 'AUTH_START' });
        
        const response = await api.post('/auth/register', userData);
        const { token, user } = response.data;
        
        localStorage.setItem('token', token);
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user, token }
        });
        
        toast.success('Registration successful!');
        
        // Clear the request ref after success
        registerRequestRef.current = null;
        
        return { success: true };
      } catch (error) {
        // Clear the request ref on error
        registerRequestRef.current = null;
        
        // Handle rate limiting gracefully
        if (error.response?.status === 429) {
          const message = 'Too many registration attempts. Please wait a moment and try again.';
          dispatch({ type: 'AUTH_FAILURE', payload: message });
          toast.error(message);
          return { success: false, error: message };
        }
        
        const message = error.response?.data?.message || 'Registration failed';
        dispatch({ type: 'AUTH_FAILURE', payload: message });
        toast.error(message);
        return { success: false, error: message };
      }
    })();
    
    // Store the promise
    registerRequestRef.current = registerPromise;
    
    return registerPromise;
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  };

  // The PUT /users/:id endpoint only confirms success — it doesn't echo the
  // updated row back — so merge the fields we just sent rather than relying
  // on a response.data.user that never arrives.
  const updateProfile = async ({ firstName, lastName, avatarUrl }) => {
    try {
      await api.put(`/users/${state.user.user_id}`, { firstName, lastName, avatarUrl });
      const patch = {};
      if (firstName) patch.first_name = firstName;
      if (lastName) patch.last_name = lastName;
      if (avatarUrl) patch.avatar_url = avatarUrl;
      dispatch({ type: 'UPDATE_USER', payload: patch });
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Real file upload (POST multipart) — distinct from updateProfile's
  // avatarUrl field, which only accepts an already-hosted image URL.
  const uploadAvatar = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/users/${state.user.user_id}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      dispatch({ type: 'UPDATE_USER', payload: { avatar_url: response.data.avatarUrl } });
      toast.success('Avatar updated successfully');
      return { success: true, avatarUrl: response.data.avatarUrl };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to upload avatar';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const getNotificationSettings = async () => {
    try {
      const response = await api.get(`/notifications/settings/user/${state.user.user_id}`);
      return { success: true, settings: response.data.settings };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load notification settings';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateNotificationSettings = async (settings) => {
    try {
      await api.put(`/notifications/settings/user/${state.user.user_id}`, { settings });
      toast.success('Notification settings saved');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save notification settings';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const getPreferences = async () => {
    try {
      const response = await api.get(`/users/${state.user.user_id}/preferences`);
      return { success: true, preferences: response.data.preferences };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load preferences';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updatePreferences = async (preferences) => {
    try {
      await api.put(`/users/${state.user.user_id}/preferences`, { preferences });
      toast.success('Preferences saved');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save preferences';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.put('/auth/change-password', {
        currentPassword,
        newPassword
      });
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('Password reset email sent');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send reset email';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await api.post('/auth/reset-password', { token, newPassword });
      toast.success('Password reset successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password reset failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verifyEmail = async (token) => {
    try {
      await api.post('/auth/verify-email', { token });

      if (state.user) {
        dispatch({
          type: 'UPDATE_USER',
          payload: { email_verified: true }
        });
      }

      toast.success('Email verified successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Email verification failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resendVerification = async (email) => {
    try {
      await api.post('/auth/resend-verification', { email });
      toast.success('If your account is unverified, a verification email has been sent');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to resend verification email';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    uploadAvatar,
    getNotificationSettings,
    updateNotificationSettings,
    getPreferences,
    updatePreferences,
    changePassword,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


